import $ from 'jquery'
import debounce from 'lodash/debounce'

import { castToFloatLocalized } from '@app/utils/string-helpers'
import { validateInput } from '@app/application/reconciliation/handlers/validators/text-input-validator'
import {
  TAB,
  ENTER,
  NUMERIC_INPUTS,
  VALIDATION_DELAY,
  DEFAULT_EXTERNAL_COMPANY_ID_ATTR,
} from '@app/application/reconciliation/constants'
import { showExternalCompanyDefault } from '@app/application/reconciliation/handlers/shared/utils'
import CompanyLinksService from '@app/application/reconciliation/services/company-links-service'

export default class LiveInputsTextInput {
  constructor(liveInputs) {
    this.liveInputs = liveInputs
    this.inputUpdated = false
    this.companyLinksService = CompanyLinksService()
    this.handleInputFocus = this.handleInputFocus.bind(this)
    this.handleKeydownEvent = this.handleKeydownEvent.bind(this)
    this.handleKeyupEvent = this.handleKeyupEvent.bind(this)
    this.debouncedHandleKeyupEvent = debounce(
      this.handleKeyupEvent,
      VALIDATION_DELAY,
      { leading: true }
    )
    this.initializeListeners()
    this.showInputValidations()
    this.showExternalCompanyDefaults()
    this.isCurrentlyBeingValidated = false
  }

  inputFilter() {
    return "[data-behaviour~='live-inputs-text-input']"
  }

  showInputValidations() {
    this.liveInputs.$container
      .find(this.inputFilter())
      .each((_index, element) => {
        validateInput(element)
      })
  }

  async showExternalCompanyDefaults() {
    const elements = this.liveInputs.$container
      .find(this.inputFilter())
      .filter(
        (_index, element) =>
          !element.hasAttribute('data-default-changed') &&
          element.hasAttribute(DEFAULT_EXTERNAL_COMPANY_ID_ATTR)
      )

    if (elements.length === 0) {
      return
    }

    const existingCompanyLinks =
      await this.companyLinksService.fetchExistingCompanyLinks()

    elements.each((_index, element) => {
      const companyId = parseInt(
        element.getAttribute(DEFAULT_EXTERNAL_COMPANY_ID_ATTR),
        10
      )
      const company = existingCompanyLinks.find(
        (companyLink) => companyId === companyLink.sourceCompanyId
      )

      if (!company) {
        return
      }

      showExternalCompanyDefault(element, company.sourceCompanyName)
    })
  }

  initializeListeners() {
    this.liveInputs.on('keydown', this.inputFilter(), this.handleKeydownEvent)
    this.liveInputs.on(
      'keyup',
      this.inputFilter(),
      this.debouncedHandleKeyupEvent
    )
    this.liveInputs.on('paste', this.inputFilter(), this.handleKeydownEvent)
    this.liveInputs.on('focus', this.inputFilter(), this.handleInputFocus)
    this.liveInputs.on('blur', this.inputFilter(), (event) => {
      this.update($(event.target))
      if (!this.isCurrentlyBeingValidated) {
        validateInput(event.target)
      }
      return this.liveInputs.removeNamespaceSelectedClass()
    })
  }

  handleInputFocus(event) {
    return this.liveInputs.focus($(event.target))
  }

  handleKeyupEvent(event) {
    this.isCurrentlyBeingValidated = true
    validateInput(event.target)
    this.isCurrentlyBeingValidated = false
  }

  handleKeydownEvent(event) {
    const $input = $(event.target)
    this.initializeOriginalValue($input)

    if (event.shiftKey && event.key === 'Enter') {
      const name = $input.attr('data-name') || ''
      const namespace = name.split('.')[0]
      if (namespace.length > 0) {
        const $link = this.liveInputs.$container
          .find(
            `a[data-behaviour="live-inputs-new-row-link"][data-name^="${namespace}."]`
          )
          .first()
        if ($link.length > 0) {
          event.preventDefault()
          this.update($input)
          $link.click()
          return
        }
      }
    }

    if (
      $input.val() !== $input.data('originalValue') &&
      (event.which === TAB || event.which === ENTER)
    ) {
      const doneEditing = $input.is('textarea')
        ? event.which === TAB || (event.which === ENTER && event.metaKey)
        : true

      if (doneEditing) {
        this.updateTabDirection(event)

        this.update($input)
      }
    }

    return this.liveInputs.resizeInput($input, true)
  }

  teardown() {
    this.liveInputs.off('keydown', this.inputFilter(), this.handleKeydownEvent)
    this.liveInputs.off(
      'keyup',
      this.inputFilter(),
      this.debouncedHandleKeyupEvent
    )
    this.liveInputs.off('focus', this.inputFilter(), this.handleInputFocus)
    if (this.debouncedHandleKeyupEvent.cancel) {
      this.debouncedHandleKeyupEvent.cancel()
    }
    this.isCurrentlyBeingValidated = false
  }

  update($input) {
    this.initializeOriginalValue($input)
    if ($input.val() !== $input.data('originalValue')) {
      if (this.inputUpdated || !this.valueRemainsDefault($input)) {
        $input.data('originalValue', $input.val())
        this.inputUpdated = true
        return this.liveInputs.update($input)
      }
    }
  }

  valueRemainsDefault($input) {
    const defaultValueStr = $input.data('defaultValue')
    if (defaultValueStr === undefined) {
      return false
    }

    const currentValueStr = $input.val()
    const originalValueStr = $input.data('originalValue')

    if (NUMERIC_INPUTS.includes($input.data('valueType'))) {
      const precision = $input.data('precision') || 2
      const currentValue =
        castToFloatLocalized(currentValueStr).toFixed(precision)
      const defaultValue =
        castToFloatLocalized(defaultValueStr).toFixed(precision)

      if (originalValueStr) {
        const originalValue =
          castToFloatLocalized(originalValueStr).toFixed(precision)
        if (originalValue !== defaultValue) return false // Value was not default so changing to default is allowed
      }

      return currentValue === defaultValue
    } else {
      if (originalValueStr) {
        if (originalValueStr !== defaultValueStr) return false // Value was not default so changing to default is allowed
      }

      return currentValueStr === defaultValueStr
    }
  }

  updateTabDirection(event) {
    const tabDirection = event.shiftKey ? -1 : 1
    return this.liveInputs.setTabDirection(tabDirection)
  }

  initializeOriginalValue($input) {
    if ($input.data('originalValue') === undefined) {
      return $input.data('originalValue', $input.val())
    }
  }
}
